// Country-aware party rules shared by the party modal, the party tables and the contract payload.
//
// A party is "international" when its phone number is outside Saudi Arabia. Every KSA-only rule
// (Saudi mobile format, short national address, SA IBAN, 7-prefixed unified number) applies to
// Saudi parties only; international parties get free-text address/registration number, any valid
// IBAN, and a bank country + name (paid out by manual transfer).
//
// Backend contract: everything is flat on the party — `international`, `iban`, `bankCountry`,
// `bankName`, `bankDocument`; the account holder is the party's `sudoName`.
import { isValidSaudiPhoneNumber } from "./phoneNumber.helpers";
import { SAUDI_COUNTRY_CODE } from "./phoneRuleConfig.helpers";

export const SAUDI_NATIONAL_ADDRESS_PATTERN = /^[A-Za-z]{4}[0-9]{4}$/;
export const SAUDI_IBAN_PATTERN = /^SA\d{22}$/i;
export const SAUDI_UNIFIED_NUMBER_PATTERN = /^7\d{9}$/;

// E.164: "+" then up to 15 digits. Per-country rules are enforced in the party modal, where
// the phone rule configs are loaded; this is the synchronous sanity check for table rows.
const INTERNATIONAL_PHONE_PATTERN = /^\+[1-9]\d{6,14}$/;

export const INTERNATIONAL_ADDRESS_MAX_LENGTH = 250;
export const INTERNATIONAL_REGISTRATION_NUMBER_MAX_LENGTH = 50;
export const INTERNATIONAL_IBAN_MAX_LENGTH = 50;
export const INTERNATIONAL_BANK_NAME_MAX_LENGTH = 100;
export const INTERNATIONAL_BRANCH_NAME_MAX_LENGTH = 100;

// ISO 9362 BIC: 4-letter bank code, 2-letter country, 2-char location, optional 3-char branch.
const SWIFT_CODE_PATTERN = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

/** The optional bank-info document an international party may attach. */
export interface IPartyBankDocument {
  bucketName: string;
  objectName: string;
  fileName?: string;
}

/** The international-only party fields, flat on the party as the backend expects. */
export interface IPartyInternationalFields {
  /** Set by the backend from the phone; sent as a hint for non-Saudi phones. */
  international?: boolean;
  /** ISO 3166-1 alpha-2, upper case, e.g. "EG". */
  bankCountry?: string;
  bankName?: string;
  /** SWIFT / BIC of the receiving bank — optional here, the party can add it in the client app. */
  swiftCode?: string;
  /** Optional; some sending banks ask for it on international transfers. */
  bankBranchName?: string;
  bankDocument?: IPartyBankDocument | null;
}

/**
 * Numbers saved before the country picker carry no "+" and were always Saudi, so only an
 * explicit non-966 "+" prefix makes a party international.
 */
export const isInternationalPhoneNumber = (phoneNumber?: string | number) => {
  const raw = `${phoneNumber ?? ""}`.replace(/\s/g, "");
  return raw.startsWith("+") && !raw.startsWith(`+${SAUDI_COUNTRY_CODE}`);
};

/** Party phone check usable without the phone rule configs (party tables, draft actions). */
export const isValidPartyPhoneNumber = (phoneNumber?: string | number) => {
  const raw = `${phoneNumber ?? ""}`.replace(/\s/g, "");
  if (isInternationalPhoneNumber(raw)) {
    return INTERNATIONAL_PHONE_PATTERN.test(raw);
  }
  return isValidSaudiPhoneNumber(raw);
};

/**
 * Saudi parties use the 8-character short address; anyone else enters free text. Brokers have no
 * editable address, so nothing about it can block them.
 */
export const isValidPartyNationalAddress = (party: {
  phoneNumber?: string | number;
  nationalAddress?: string | null;
  role?: string;
}) => {
  if (!party?.nationalAddress || party?.role === "BROKER") return true;
  if (isInternationalPhoneNumber(party.phoneNumber)) {
    return (
      party.nationalAddress.trim().length <= INTERNATIONAL_ADDRESS_MAX_LENGTH
    );
  }
  return SAUDI_NATIONAL_ADDRESS_PATTERN.test(party.nationalAddress);
};

/** Strips whitespace and upper-cases, the only normalisation applied to any IBAN. */
export const normalizeIban = (iban?: string) =>
  `${iban ?? ""}`.replace(/\s/g, "").toUpperCase();

/** Upper-cases and strips whitespace, the only normalisation applied to a SWIFT code. */
export const normalizeSwiftCode = (swiftCode?: string | null) =>
  `${swiftCode ?? ""}`.replace(/\s/g, "").toUpperCase();

/** 8 or 11 characters per ISO 9362; the country part isn't checked against the bank country. */
export const isValidSwiftCode = (swiftCode?: string | null) =>
  SWIFT_CODE_PATTERN.test(normalizeSwiftCode(swiftCode));

/**
 * ISO 13616 IBAN check (country letters, check digits, MOD-97), mirroring the backend: any
 * country's IBAN passes, a local account number does not.
 */
export const isValidIban = (iban?: string) => {
  const value = normalizeIban(iban);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(value)) return false;
  const rearranged = `${value.slice(4)}${value.slice(0, 4)}`;
  let remainder = 0;
  for (const char of rearranged) {
    const digits = /[A-Z]/.test(char) ? `${char.charCodeAt(0) - 55}` : char;
    for (const digit of digits) {
      remainder = (remainder * 10 + Number(digit)) % 97;
    }
  }
  return remainder === 1;
};

/** Legacy rows hold a bare Saudi national number; anything with a "+" is already E.164. */
export const toPartyPhoneNumber = (phoneNumber?: string | number) => {
  const raw = `${phoneNumber ?? ""}`.trim().replace(/\s/g, "");
  if (!raw || raw.startsWith("+")) return raw;
  if (/^9665\d{8}$/.test(raw)) return `+${raw}`;
  return `+${SAUDI_COUNTRY_CODE}${raw.replace(/^0/, "")}`;
};

/** International per the backend's echoed flag, or by phone for parties not saved yet. */
export const isInternationalParty = (party: {
  international?: boolean;
  phoneNumber?: string | number;
}) => !!party?.international || isInternationalPhoneNumber(party?.phoneNumber);
